import React from 'react';
import { formatYValue } from '../utils/formatting';

interface AxesRendererProps {
  width: number;
  height: number;
  margin: { top: number; right: number; bottom: number; left: number };
  xValues: (string | number)[];
  yTicks: number[];
  xScale: (value: string | number) => number;
  yScale: (value: string | number) => number;
  yAxisPrefix?: string;
  yAxisSuffix?: string;
  chartType?: string; // Add chart type to handle horizontal bar charts
  reversed?: boolean; // Add reversed prop to handle proper positioning
}

/**
 * Axes and Grid Renderer Component
 */
export const AxesRenderer: React.FC<AxesRendererProps> = ({
  width,
  height,
  margin,
  xValues,
  yTicks,
  xScale,
  yScale,
  yAxisPrefix = '',
  yAxisSuffix = '',
  chartType = 'line',
  reversed = false,
}) => {
  // Check if we're using a horizontal bar chart
  const isHorizontalBarChart = chartType === 'horizontal-bar';
  return (
    <>
      {!isHorizontalBarChart ? (
        // Regular chart axes
        <>
          {/* Y-axis grid lines */}
          {yTicks.map((tick, i) => (
            <line
              key={`y-grid-${i}`}
              x1={margin.left}
              y1={yScale(tick)}
              x2={width - margin.right}
              y2={yScale(tick)}
              stroke="#7D7C7B"
              strokeWidth={1}
              opacity={0.1}
              //strokeDasharray="4 4"
            />
          ))}

          {/* Y-axis labels */}
          {yTicks.map((tick, i) => (
            <text
              key={`y-label-${i}`}
              x={margin.left - 10}
              y={yScale(tick)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="12"
              fill="#666"
            >
              {formatYValue(tick, yAxisPrefix, yAxisSuffix)}
            </text>
          ))}

          {/* X-axis grid lines */}
          {xValues.map((tick, i) => (
            <line
              key={`x-grid-${i}`}
              x1={xScale(i.toString())}
              x2={xScale(i.toString())}
              y1={margin.top}
              y2={height - margin.bottom}
              stroke="#7D7C7B"
              strokeWidth={1}
              opacity={0.1}
              //strokeDasharray="4 4"
            />
          ))}

          {/* X-axis line */}
          <line
            x1={margin.left}
            y1={height - margin.bottom}
            x2={width - margin.right}
            y2={height - margin.bottom}
            stroke="#333"
            strokeWidth={1}
            opacity={1}
          />

          {/* X-axis labels */}
          {xValues.map((value, i) => {
            const valueStr = String(value);
            const parts = valueStr.split(" '");
            // When reversed, we need to map the label index to the correct position
            const positionIndex = reversed ? xValues.length - 1 - i : i;
            const xPosition = xScale(positionIndex.toString());

            // Check if this is a multi-line label (contains our separator)
            if (parts.length > 1) {
              return (
                <g key={`x-label-${i}`}>
                  <text x={xPosition} y={height - margin.bottom + 20} textAnchor="middle" fontSize="12" fill="#666">
                    {parts[0]}
                  </text>
                  <text x={xPosition} y={height - margin.bottom + 35} textAnchor="middle" fontSize="12" fill="#111">
                    {parts[1]}
                  </text>
                </g>
              );
            }

            // Regular single-line label
            return (
              <text
                key={`x-label-${i}`}
                x={xPosition}
                y={height - margin.bottom + 20}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
              >
                {valueStr}
              </text>
            );
          })}
        </>
      ) : (
        // Horizontal bar chart axes
        <>
          {/* X-axis grid lines (values) */}
          {yTicks.map((tick, i) => (
            <line
              key={`x-grid-${i}`}
              x1={xScale(tick)}
              y1={margin.top}
              x2={xScale(tick)}
              y2={height - margin.bottom}
              stroke="#e0e0e0"
              strokeDasharray="4 4"
            />
          ))}

          {/* X-axis labels (values) */}
          {yTicks.map((tick, i) => (
            <text
              key={`x-label-${i}`}
              x={xScale(tick)}
              y={height - margin.bottom + 15}
              textAnchor="middle"
              fontSize="12"
              fill="#666"
            >
              {formatYValue(tick, yAxisPrefix, yAxisSuffix)}
            </text>
          ))}

          {/* Y-axis line */}
          <line x1={margin.left} y1={margin.top} x2={margin.left} y2={height - margin.bottom} stroke="#999" />

          {/* Y-axis labels (categories) */}
          {xValues.map((value, i) => (
            <text
              key={`y-label-${i}`}
              x={margin.left - 10}
              y={yScale(i)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="12"
              fill="#666"
            >
              {String(value)}
            </text>
          ))}
        </>
      )}
    </>
  );
};

export default AxesRenderer;
